import { useContext, useEffect, useRef, useState } from "react";
import DxmlElement from "../DxmlElement";
import { useEditorFile } from "../../../../../hooks";
import { useTranslation } from 'react-i18next';
import { resources, translations } from "../../../../../translations/translationsConstants";
import { ClipboardDocumentIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { PageElementContext, WebEditorContext } from "../../../../../contexts";
import * as Constants from "../../../../../utils/constants";
import { useClipboardSafe } from "../../../../../hooks/useClipboardSafe";

export default function ElementInPage({ editorBaseModel, elementsInPage, index }) {
    const editorFileManager = useEditorFile();
    const { t: tc } = useTranslation([resources.common]);
    const elementRef = useRef();
    const { editorFileRef,
        formikValuesRef,
        rightClickMenu,
        setRightClickMenu,
        autoSaveEditorFile } = useContext(WebEditorContext);
    const disabled = editorFileRef.current?.DxmlFileStatus !== Constants.fileStatus.draft;
    const { canPaste, pasteText, saveSelectedElement } = useClipboardSafe();

    const menuItems = [
        [
            {
                label: tc(translations.common.addPageBreakBefore),
                icon: <PlusIcon />,
                disabled: !editorFileManager.canAddPageBreakBefore(editorFileRef.current, editorBaseModel),
                onClick: () => {
                    editorFileManager.addPageBreakBefore(editorFileRef.current, editorBaseModel);
                    autoSaveEditorFile();
                },
            },
            {
                label: tc(translations.common.addPageBreakAfter),
                icon: <PlusIcon />,
                disabled: !editorFileManager.canAddPageBreakAfter(editorFileRef.current, editorBaseModel),
                onClick: () => {
                    editorFileManager.addPageBreakAfter(editorFileRef.current, editorBaseModel);
                    autoSaveEditorFile();
                },
            },
        ],
        [
            {
                label: tc(translations.common.removePageBreakBefore),
                icon: <TrashIcon />,
                disabled: !editorFileManager.canRemovePageBreakBefore(editorFileRef.current, editorBaseModel),
                onClick: () => {
                    editorFileManager.removePageBreakBefore(editorFileRef.current, editorBaseModel);
                    autoSaveEditorFile();
                },
            },
            {
                label: tc(translations.common.removePageBreakAfter),
                icon: <TrashIcon />,
                disabled: !editorFileManager.canRemovePageBreakAfter(editorFileRef.current, editorBaseModel),
                onClick: () => {
                    editorFileManager.removePageBreakAfter(editorFileRef.current, editorBaseModel);
                    autoSaveEditorFile();
                },
            },
        ],
        [
            {
                label: tc(translations.common.paste),
                icon: <ClipboardDocumentIcon />,
                disabled: () => !canPaste(),
                onClick: async () => {
                    await pasteText(editorFileRef, formikValuesRef);
                    autoSaveEditorFile();
                },
            },
        ],
    ];



    const handleContextMenu = (e) => {
        e.preventDefault(); // prevent the default behaviour when right clicked

        if (elementRef.current)
            elementRef.current.style.border = "1px dashed #4338CA";

        saveSelectedElement(e.selectedElement);

        let mergedMenuItems = menuItems;
        if (e.extraMenuItems?.length > 0) {
            mergedMenuItems = menuItems.concat(e.extraMenuItems);
        }

        setRightClickMenu({
            position: {
                x: e.pageX,
                y: e.pageY,
            },
            items: mergedMenuItems,
        });
    }

    const isInsideElement = () => {
        const rect = elementRef.current.getBoundingClientRect();
        if (!rightClickMenu?.position) return false;
        return (
            rightClickMenu.position.x >= rect.left &&
            rightClickMenu.position.x <= rect.right &&
            rightClickMenu.position.y >= rect.top &&
            rightClickMenu.position.y <= rect.bottom
        );
    }

    useEffect(() => {
        if (!elementRef.current)
            return;

        if (!rightClickMenu) {
            elementRef.current.style.border = "";
        } else {
            if (!isInsideElement())
                elementRef.current.style.border = "";
        }
    }, [rightClickMenu])

    return (
        <PageElementContext.Provider value={{
            handleContextMenu: handleContextMenu,
        }}>
            <div
                id={`elementInPage${editorBaseModel.Id}`}
                ref={elementRef}
                tabIndex={disabled ? -1 : 0}
                className={`${disabled ? "tw-pointer-events-none tw-cursor-not-allowed" : ""}`}
                onContextMenu={handleContextMenu}
            >
                <DxmlElement
                    editorBaseModel={editorBaseModel}
                />
                <span id="context-hint" className="tw-hidden">{tc(translations.common.blockHelpText)}</span>
            </div>
        </PageElementContext.Provider>
    )
}
